import { useState } from "react";
import type { Post } from "../lib/api";

type DeviceType = "phone" | "tablet" | "desktop";

interface PostPreviewProps {
  post: Post;
  imageUrl?: string;
  brandName?: string;
  brandColors?: { primary: string; secondary: string };
}

const DEVICE_CONFIGS: Record<
  DeviceType,
  { label: string; width: number; icon: string }
> = {
  phone: { label: "Phone", width: 375, icon: "📱" },
  tablet: { label: "Tablet", width: 540, icon: "⬜" },
  desktop: { label: "Desktop", width: 680, icon: "🖥" },
};

// ─── Platform renderers ────────────────────────────────────────────────────────

function AvatarPlaceholder({ name, color }: { name: string; color?: string }) {
  const initials = name.slice(0, 2).toUpperCase();
  return (
    <div
      className="rounded-full w-9 h-9 flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
      style={{ backgroundColor: color ?? "#6366f1" }}
    >
      {initials}
    </div>
  );
}

function InstagramFeedPreview({
  post,
  imageUrl,
  brandName,
  brandColors,
}: PostPreviewProps) {
  const displayName = brandName ?? "Your Brand";
  const hashtagText = post.hashtags.map((h) => `#${h}`).join(" ");

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden font-sans">
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        <AvatarPlaceholder name={displayName} color={brandColors?.primary} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink truncate">
            {displayName}
          </p>
          <p className="text-xs text-faint">Sponsored</p>
        </div>
        <span className="text-faint text-lg">•••</span>
      </div>

      {/* Image */}
      <div className="w-full aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Post visual"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-300">
            <svg
              className="w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-xs">Add an image</span>
          </div>
        )}
      </div>

      {/* Engagement bar */}
      <div className="flex items-center gap-3 px-3 py-2">
        <span className="text-2xl">🤍</span>
        <span className="text-2xl">💬</span>
        <span className="text-2xl">📤</span>
        <span className="ml-auto text-2xl">🔖</span>
      </div>
      <p className="px-3 text-sm font-semibold text-ink pb-1">1,024 likes</p>

      {/* Caption */}
      <div className="px-3 pb-3">
        <p className="text-sm text-ink leading-relaxed">
          <span className="font-semibold">{displayName}</span> {post.copy}
        </p>
        {hashtagText && (
          <p
            className="text-sm mt-1"
            style={{ color: brandColors?.primary ?? "#3b82f6" }}
          >
            {hashtagText}
          </p>
        )}
        <p className="text-xs text-faint mt-1.5 uppercase tracking-wide">
          2 hours ago
        </p>
      </div>
    </div>
  );
}

function InstagramStoryPreview({
  post,
  imageUrl,
  brandName,
  brandColors,
}: PostPreviewProps) {
  const displayName = brandName ?? "Your Brand";

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden text-white flex flex-col"
      style={{
        aspectRatio: "9/16",
        background: brandColors?.primary ?? "#6366f1",
      }}
    >
      {/* Background image */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt="Story background"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />

      {/* Story bar */}
      <div className="relative z-10 px-3 pt-3">
        <div className="w-full h-0.5 bg-white/40 rounded-full mb-3">
          <div className="w-1/3 h-full bg-white rounded-full" />
        </div>
        <div className="flex items-center gap-2">
          <AvatarPlaceholder
            name={displayName}
            color={brandColors?.secondary ?? "#fff"}
          />
          <div>
            <p className="text-xs font-semibold">{displayName}</p>
            <p className="text-xs text-white/70">2h</p>
          </div>
          <span className="ml-auto text-white/80">✕</span>
        </div>
      </div>

      {/* Copy overlay */}
      <div className="relative z-10 mt-auto px-5 pb-16">
        <p className="text-base font-semibold leading-snug text-shadow drop-shadow-lg">
          {post.copy.slice(0, 150)}
          {post.copy.length > 150 ? "…" : ""}
        </p>
        {post.hashtags.length > 0 && (
          <p className="text-xs text-white/80 mt-1.5">
            {post.hashtags
              .slice(0, 3)
              .map((h) => `#${h}`)
              .join(" ")}
          </p>
        )}
        {/* Swipe up CTA */}
        <div className="mt-4 flex flex-col items-center gap-1">
          <span className="text-white text-xl">∧</span>
          <span className="text-xs font-semibold uppercase tracking-widest">
            Swipe Up
          </span>
        </div>
      </div>
    </div>
  );
}

function LinkedInPreview({
  post,
  imageUrl,
  brandName,
  brandColors,
}: PostPreviewProps) {
  const displayName = brandName ?? "Your Brand";

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden font-sans">
      {/* Header */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-3">
        <AvatarPlaceholder name={displayName} color={brandColors?.primary} />
        <div className="flex-1">
          <p className="text-sm font-semibold text-ink">{displayName}</p>
          <p className="text-xs text-muted">Company · Sponsored</p>
          <p className="text-xs text-faint flex items-center gap-1 mt-0.5">
            <span>🌐</span> Public
          </p>
        </div>
        <span className="text-faint">•••</span>
      </div>

      {/* Copy */}
      <div className="px-4 pb-3">
        <p className="text-sm text-ink leading-relaxed whitespace-pre-line">
          {post.copy}
        </p>
        {post.hashtags.length > 0 && (
          <p
            className="text-sm mt-2"
            style={{ color: brandColors?.primary ?? "#0a66c2" }}
          >
            {post.hashtags.map((h) => `#${h}`).join(" ")}
          </p>
        )}
      </div>

      {/* Image */}
      {imageUrl && (
        <div className="w-full aspect-video overflow-hidden bg-gray-100">
          <img
            src={imageUrl}
            alt="Post visual"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      {!imageUrl && (
        <div className="mx-4 mb-3 w-full aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center text-gray-300">
          <span className="text-xs">Image placeholder</span>
        </div>
      )}

      {/* Engagement bar */}
      <div className="px-4 py-2 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs text-muted pb-2">
          <span>👍 234 · 💬 18 comments</span>
          <span>7 reposts</span>
        </div>
        <div className="flex items-center justify-around pt-2 border-t border-gray-100">
          {["👍 Like", "💬 Comment", "↗ Repost", "📤 Send"].map((action) => (
            <button
              key={action}
              className="text-xs text-slate font-medium hover:text-ink"
            >
              {action}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TwitterPreview({
  post,
  imageUrl,
  brandName,
  brandColors,
}: PostPreviewProps) {
  const displayName = brandName ?? "Your Brand";
  const handle = displayName.toLowerCase().replace(/\s+/g, "");

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden font-sans p-4">
      {/* Header */}
      <div className="flex gap-3">
        <AvatarPlaceholder name={displayName} color={brandColors?.primary} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold text-ink">{displayName}</span>
            <span className="text-blue-400 text-xs">✓</span>
          </div>
          <p className="text-xs text-muted">@{handle} · 2h</p>
        </div>
        <span className="text-faint text-lg font-bold">𝕏</span>
      </div>

      {/* Copy */}
      <div className="mt-3">
        <p className="text-sm text-ink leading-relaxed">
          {post.copy.slice(0, 280)}
          {post.hashtags.length > 0 && (
            <span style={{ color: brandColors?.primary ?? "#1d9bf0" }}>
              {" "}
              {post.hashtags
                .slice(0, 2)
                .map((h) => `#${h}`)
                .join(" ")}
            </span>
          )}
        </p>
      </div>

      {/* Image card */}
      {imageUrl && (
        <div className="mt-3 rounded-2xl overflow-hidden border border-gray-200 aspect-video">
          <img
            src={imageUrl}
            alt="Post visual"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      {!imageUrl && (
        <div className="mt-3 rounded-2xl border border-gray-200 aspect-video bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center text-gray-300 text-xs">
          Image placeholder
        </div>
      )}

      {/* Engagement bar */}
      <div className="flex items-center justify-between mt-3 text-muted text-xs pt-2 border-t border-gray-100">
        <span>💬 42</span>
        <span>🔁 128</span>
        <span>🤍 1.4K</span>
        <span>📊 12K</span>
        <span>📤</span>
      </div>
    </div>
  );
}

function FacebookPreview({
  post,
  imageUrl,
  brandName,
  brandColors,
}: PostPreviewProps) {
  const displayName = brandName ?? "Your Brand";

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden font-sans">
      {/* Header */}
      <div className="flex items-start gap-3 px-4 pt-4 pb-3">
        <AvatarPlaceholder name={displayName} color={brandColors?.primary} />
        <div className="flex-1">
          <p className="text-sm font-semibold text-ink">{displayName}</p>
          <p className="text-xs text-muted flex items-center gap-1">
            <span>2h</span> · <span>🌐</span>
          </p>
        </div>
        <span className="text-faint">•••</span>
      </div>

      {/* Copy */}
      <div className="px-4 pb-3">
        <p className="text-sm text-ink leading-relaxed">{post.copy}</p>
        {post.hashtags.length > 0 && (
          <p
            className="text-sm mt-1.5"
            style={{ color: brandColors?.primary ?? "#1877f2" }}
          >
            {post.hashtags.map((h) => `#${h}`).join(" ")}
          </p>
        )}
      </div>

      {/* Image */}
      {imageUrl ? (
        <div className="w-full aspect-video overflow-hidden bg-gray-100">
          <img
            src={imageUrl}
            alt="Post visual"
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="w-full aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-gray-300 text-xs">
          Image placeholder
        </div>
      )}

      {/* Reaction count */}
      <div className="px-4 py-2 text-xs text-muted flex justify-between">
        <span>👍 ❤️ 😮 892</span>
        <span>56 comments · 12 shares</span>
      </div>

      {/* Action bar */}
      <div className="border-t border-gray-100 px-4 py-2">
        <div className="flex items-center justify-around">
          {["👍 Like", "💬 Comment", "↗ Share"].map((a) => (
            <button
              key={a}
              className="text-xs font-semibold text-slate hover:text-ink"
            >
              {a}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Platform dispatcher ───────────────────────────────────────────────────────

function PlatformMockup(props: PostPreviewProps) {
  switch (props.post.platform) {
    case "instagram_feed":
      return <InstagramFeedPreview {...props} />;
    case "instagram_story":
      return <InstagramStoryPreview {...props} />;
    case "linkedin":
      return <LinkedInPreview {...props} />;
    case "twitter":
      return <TwitterPreview {...props} />;
    case "facebook":
      return <FacebookPreview {...props} />;
    default:
      return <InstagramFeedPreview {...props} />;
  }
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function PostPreview(props: PostPreviewProps) {
  const [device, setDevice] = useState<DeviceType>("phone");
  const cfg = DEVICE_CONFIGS[device];

  return (
    <div className="flex flex-col gap-4">
      {/* Device tabs */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl self-center">
        {(
          Object.entries(DEVICE_CONFIGS) as [
            DeviceType,
            (typeof DEVICE_CONFIGS)[DeviceType],
          ][]
        ).map(([key, val]) => (
          <button
            key={key}
            onClick={() => setDevice(key)}
            aria-label={`Preview on ${val.label}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              device === key
                ? "bg-white shadow-sm text-ink"
                : "text-muted hover:text-slate"
            }`}
          >
            <span>{val.icon}</span>
            <span>{val.label}</span>
          </button>
        ))}
      </div>

      {/* Scrollable frame */}
      <div className="overflow-x-auto pb-2">
        <div
          className="mx-auto transition-all duration-300"
          style={{ width: cfg.width, maxWidth: "100%" }}
        >
          <PlatformMockup {...props} />
        </div>
      </div>
    </div>
  );
}
